import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Trash2, Sparkles, Image as ImageIcon, Check } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { salesApi, apiError, fileUrl } from '../../salesApi';
import { useT } from '../../salesI18n';
import { EmptyState } from '../EmptyState';
import { PhotoUploader } from './PhotoUploader';
import { SimulationJobCard } from './SimulationJobCard';
import { WindowConfigDialog, WindowMarkers, markerLabels } from './WindowConfigDialog';
import { DetectProgress } from '../../../components/DetectProgress';
import { OpeningFormDialog } from '../measurement/OpeningFormDialog';
import { Settings2, ScanSearch } from 'lucide-react';

// A simulação segue a configuração feita janela por janela (Configurar janelas); o painel só escolhe as cores das variações
const GLOBAL_DEFAULTS = { window_style: 'keep', grille: 'none', glass_type: 'clear', screen: 'none', exterior_trim: null, interior_trim: null };
export const isCustomized = (w) => w.replace === false || w.is_new || w.style || w.grille || w.glass_type || w.screen || w.exterior_trim || w.interior_trim;

const ColorPicker = ({ title, colors, selected, onChange, testPrefix }) => {
  const { t } = useT();
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <div data-testid={`${testPrefix}-colors`}>
      <div className="flex items-center justify-between mb-2">
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">{title} <span className="font-mono">({selected.length}/{colors.length})</span></Label>
        <div className="flex gap-2 text-[11px] font-semibold">
          <button type="button" onClick={() => onChange(colors.map((c) => c.id))} className="text-primary hover:underline" data-testid={`${testPrefix}-select-all`}>{t.selectAll}</button>
          <button type="button" onClick={() => onChange([])} className="text-muted-foreground hover:underline" data-testid={`${testPrefix}-select-none`}>{t.selectNone}</button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {colors.map((c) => {
          const on = selected.includes(c.id);
          return (
            <button key={c.id} type="button" onClick={() => toggle(c.id)} data-testid={`${testPrefix}-color-${c.id}`} aria-pressed={on} className={`inline-flex items-center gap-2 h-9 pl-1.5 pr-3 rounded-full border text-xs font-semibold transition-all ${on ? 'border-primary bg-accent text-accent-foreground' : 'border-border text-muted-foreground opacity-70 hover:opacity-100'}`}>
              <span className="relative h-6 w-6 rounded-full border border-black/10 flex items-center justify-center" style={{ background: c.hex }}>{on && <Check className="h-3.5 w-3.5 text-white drop-shadow" />}</span>
              {c.name}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const SimulationPanel = ({ lead, onLeadChanged }) => {
  const { t } = useT();
  const [photos, setPhotos] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [palette, setPalette] = useState(null);
  const [selected, setSelected] = useState([]);
  const [extColors, setExtColors] = useState([]);
  const [intColors, setIntColors] = useState([]);
  const [starting, setStarting] = useState(false);
  const [winCfg, setWinCfg] = useState(null); // {photo, n?}
  const [openings, setOpenings] = useState([]);
  const [editOpening, setEditOpening] = useState(null);
  const pollRef = useRef(null);
  const detectPollRef = useRef(null);

  const loadPhotos = useCallback(async () => { const { data } = await salesApi.get(`/leads/${lead.id}/photos`); setPhotos(data); setSelected((s) => (s.length ? s : data.map((p) => p.id))); return data; }, [lead.id]);
  const loadJobs = useCallback(async () => { const { data } = await salesApi.get(`/leads/${lead.id}/simulations`); setJobs(data); return data; }, [lead.id]);
  const loadOpenings = useCallback(async () => { const { data } = await salesApi.get(`/leads/${lead.id}/openings`); setOpenings(data); return data; }, [lead.id]);

  // Toque no marcador da foto: abre a abertura vinculada; sem vínculo → configurar janelas já naquela janela
  const pickWindow = (p, n) => {
    const w = (p.windows || []).find((x) => x.n === n);
    const op = w?.opening_id && openings.find((o) => o.id === w.opening_id);
    if (op) setEditOpening(op); else setWinCfg({ photo: p, n });
  };

  const anyDetecting = photos.some((p) => p.windows_status === 'detecting');
  useEffect(() => {
    clearInterval(detectPollRef.current);
    if (anyDetecting) detectPollRef.current = setInterval(() => { loadPhotos().catch(() => {}); }, 4000);
    return () => clearInterval(detectPollRef.current);
  }, [anyDetecting, loadPhotos]);

  const detectNow = async (p) => {
    setPhotos((ps) => ps.map((x) => (x.id === p.id ? { ...x, windows_status: 'detecting', windows_started_at: new Date().toISOString() } : x)));
    try { const { data } = await salesApi.post(`/files/${p.id}/detect-windows`); setPhotos((ps) => ps.map((x) => (x.id === p.id ? data : x))); toast.success(t.windowsDetected(data.windows?.length || 0)); } catch (e) { toast.error(apiError(e)); loadPhotos().catch(() => {}); }
  };

  useEffect(() => {
    loadPhotos().catch(() => {});
    loadOpenings().catch(() => {});
    loadJobs().then((js) => js.filter((j) => ['done', 'partial', 'failed'].includes(j.status)).forEach((j) => salesApi.post(`/simulations/${j.id}/ack`).catch(() => {}))).catch(() => {});
    salesApi.get('/palette').then((r) => { setPalette(r.data); setExtColors(r.data.exterior.map((c) => c.id)); setIntColors(r.data.interior.map((c) => c.id)); }).catch(() => {});
  }, [loadPhotos, loadJobs, loadOpenings]);

  const anyRunning = jobs.some((j) => j.status === 'queued' || j.status === 'processing');
  useEffect(() => {
    clearInterval(pollRef.current);
    if (anyRunning) {
      pollRef.current = setInterval(async () => {
        const js = await loadJobs().catch(() => []);
        const finished = js.filter((j) => ['done', 'partial', 'failed'].includes(j.status) && jobs.find((o) => o.id === j.id && (o.status === 'queued' || o.status === 'processing')));
        if (finished.length) { toast.success(t.simReady); finished.forEach((j) => salesApi.post(`/simulations/${j.id}/ack`).catch(() => {})); onLeadChanged?.(); }
      }, 5000);
    }
    return () => clearInterval(pollRef.current);
  }, [anyRunning, jobs, loadJobs, t, onLeadChanged]);

  const kinds = useMemo(() => new Set(photos.filter((p) => selected.includes(p.id)).map((p) => p.photo_kind)), [photos, selected]);
  const totalImages = useMemo(() => photos.filter((p) => selected.includes(p.id)).reduce((n, p) => n + (p.photo_kind === 'interior' ? intColors.length : extColors.length), 0), [photos, selected, extColors, intColors]);
  const customizedCount = useMemo(() => photos.filter((p) => selected.includes(p.id)).reduce((n, p) => n + (p.windows || []).filter(isCustomized).length, 0), [photos, selected]);

  const togglePhoto = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const setKind = async (p, kind) => { try { await salesApi.put(`/files/${p.id}`, { photo_kind: kind }); setPhotos((ps) => ps.map((x) => (x.id === p.id ? { ...x, photo_kind: kind } : x))); } catch (e) { toast.error(apiError(e)); } };
  const deletePhoto = async (p) => { if (!window.confirm(t.deletePhotoConfirm)) return; try { await salesApi.delete(`/files/${p.id}`); setPhotos((ps) => ps.filter((x) => x.id !== p.id)); setSelected((s) => s.filter((x) => x !== p.id)); toast.success(t.photoDeleted); } catch (e) { toast.error(apiError(e)); } };

  const start = async () => {
    if (!selected.length) { toast.error(t.selectAtLeastOnePhoto); return; }
    if (!totalImages) { toast.error(t.selectAtLeastOneColor); return; }
    setStarting(true);
    try {
      await salesApi.post(`/leads/${lead.id}/simulations`, { photo_ids: selected, color_ids: { exterior: extColors, interior: intColors }, ...GLOBAL_DEFAULTS });
      toast.success(t.simStarted, { duration: 6000 });
      await loadJobs();
      onLeadChanged?.();
    } catch (e) { toast.error(apiError(e)); } finally { setStarting(false); }
  };

  const retry = async (job) => { try { await salesApi.post(`/simulations/${job.id}/retry`); loadJobs(); } catch (e) { toast.error(apiError(e)); } };
  const removeJob = async (job) => { if (!window.confirm(t.deleteSimConfirm)) return; try { await salesApi.delete(`/simulations/${job.id}`); loadJobs(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="space-y-6" data-testid="simulation-panel">
      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-display font-bold flex items-center gap-2"><ImageIcon className="h-4 w-4 text-primary" /> {t.photos} <span className="font-mono text-xs text-muted-foreground">({photos.length})</span></h3>
          <PhotoUploader leadId={lead.id} kind="exterior" onUploaded={(doc) => { setPhotos((ps) => [...ps, doc]); setSelected((s) => [...s, doc.id]); }} compact={photos.length > 0} />
        </div>
        {photos.length === 0 ? (
          <EmptyState icon={ImageIcon} title={t.noPhotos} hint={t.noPhotosHint} testId="simulation-no-photos" />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="lead-photos-grid">
            {photos.map((p) => {
              const on = selected.includes(p.id);
              const wins = p.windows || [];
              const custom = wins.filter(isCustomized).length;
              return (
                <div key={p.id} className={`relative rounded-xl overflow-hidden border-2 transition-colors ${on ? 'border-primary' : 'border-border opacity-80'}`} data-testid={`lead-photo-${p.id}`}>
                  <button type="button" onClick={() => togglePhoto(p.id)} className="block w-full aspect-[4/3] bg-muted relative" data-testid={`lead-photo-toggle-${p.id}`}>
                    <img src={fileUrl(p.id, 'thumb')} alt={p.caption || ''} className="w-full h-full object-cover" loading="lazy" />
                  </button>
                  {p.windows_status === 'done' && wins.length > 0 && <div className="absolute inset-x-0 top-0 aspect-[4/3] pointer-events-none"><WindowMarkers windows={wins} small cover={{ w: p.width, h: p.height, aspect: 4 / 3 }} labels={markerLabels(wins, openings)} onPick={(n) => pickWindow(p, n)} /></div>}
                  <span className={`absolute top-2 left-2 h-6 w-6 rounded-full flex items-center justify-center text-white shadow ${on ? 'bg-primary' : 'bg-black/40'}`}>{on && <Check className="h-3.5 w-3.5" />}</span>
                  <button type="button" onClick={() => deletePhoto(p)} className="absolute top-2 right-2 h-7 w-7 rounded-full bg-black/50 text-white hover:bg-rose-600 flex items-center justify-center" aria-label={t.delete} data-testid={`lead-photo-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  <div className="absolute top-10 left-2 right-2 flex items-center gap-1" data-testid={`lead-photo-windows-${p.id}`}>
                    {p.windows_status === 'detecting' && <div className="flex-1 rounded-xl bg-white/90 backdrop-blur px-2 py-1.5 text-teal-900 shadow"><DetectProgress compact startedAt={p.windows_started_at || p.created_at} label={t.detectingWindows} remainingLabel={(s) => `~${s}s`} almostLabel={t.detectAlmost} testId={`lead-photo-detect-progress-${p.id}`} /></div>}
                    {p.windows_status === 'done' && (
                      <button type="button" onClick={() => setWinCfg({ photo: p })} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold text-white shadow ${custom ? 'bg-amber-500' : 'bg-teal-600'} hover:brightness-110`} data-testid={`lead-photo-configure-${p.id}`}>
                        <Settings2 className="h-3 w-3" /> {wins.length} {t.windowsLabel}{custom ? ` · ${custom} ${t.customizedShort}` : ''}
                      </button>
                    )}
                    {(p.windows_status === 'failed' || !p.windows_status) && (
                      <button type="button" onClick={() => detectNow(p)} className="inline-flex items-center gap-1 rounded-full bg-black/60 text-white px-2 py-0.5 text-[10px] font-semibold hover:bg-black/80" data-testid={`lead-photo-detect-${p.id}`}><ScanSearch className="h-3 w-3" /> {t.detectWindows}</button>
                    )}
                  </div>
                  <div className="absolute bottom-0 inset-x-0 flex text-[11px] font-semibold">
                    {['exterior', 'interior'].map((k) => (
                      <button key={k} type="button" onClick={() => setKind(p, k)} data-testid={`lead-photo-kind-${k}-${p.id}`} className={`flex-1 h-8 ${p.photo_kind === k ? 'bg-primary text-primary-foreground' : 'bg-black/60 text-white/80 hover:bg-black/70'}`}>{t[k]}</button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {photos.length > 0 && palette && (
        <section className="bg-card rounded-xl border border-border p-4 sm:p-5 shadow-sm space-y-4" data-testid="simulation-options">
          <div>
            <h3 className="font-display font-bold flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> {t.simOptions}</h3>
            <p className="text-xs text-muted-foreground mt-1" data-testid="simulation-per-window-hint">{t.simPerWindowHint}</p>
          </div>
          {kinds.has('exterior') && <ColorPicker title={t.colorsExterior} colors={palette.exterior} selected={extColors} onChange={setExtColors} testPrefix="sim-ext" />}
          {kinds.has('interior') && <ColorPicker title={t.colorsInterior} colors={palette.interior} selected={intColors} onChange={setIntColors} testPrefix="sim-int" />}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
            <div>
              <p className="text-sm text-muted-foreground" data-testid="simulation-estimate"><b className="text-foreground font-mono">{t.willGenerate(totalImages)}</b> · {t.estTime(Math.max(1, Math.ceil((totalImages * 14) / 3 / 60)))}</p>
              {customizedCount > 0 && <p className="text-xs text-amber-700 font-semibold mt-0.5" data-testid="simulation-customized-note">{t.customizedNote(customizedCount)}</p>}
            </div>
            <Button onClick={start} disabled={starting || !totalImages} className="h-11 font-semibold" data-testid="simulation-generate-btn"><Sparkles className="h-4 w-4 mr-1.5" /> {starting ? t.generating : t.generate}</Button>
          </div>
        </section>
      )}

      <section className="space-y-3" data-testid="simulation-jobs">
        {jobs.length === 0 && photos.length > 0 && <p className="text-sm text-muted-foreground text-center py-4" data-testid="simulation-no-jobs">{t.noSimulations}</p>}
        {jobs.map((j) => <SimulationJobCard key={j.id} job={j} photos={photos} onRetry={retry} onDelete={removeJob} />)}
      </section>

      <WindowConfigDialog open={Boolean(winCfg)} onOpenChange={(o) => !o && setWinCfg(null)} photo={winCfg ? photos.find((p) => p.id === winCfg.photo.id) || winCfg.photo : null} palette={palette} globalStyle={GLOBAL_DEFAULTS.window_style} globalGrille={GLOBAL_DEFAULTS.grille} globalSpecs={GLOBAL_DEFAULTS} onSaved={(doc) => setPhotos((ps) => ps.map((x) => (x.id === doc.id ? doc : x)))}
        openings={openings} leadId={lead.id} onOpeningsChanged={() => loadOpenings().catch(() => {})} initialActive={winCfg?.n ?? null} />
      <OpeningFormDialog open={Boolean(editOpening)} onOpenChange={(o) => !o && setEditOpening(null)} leadId={lead.id} opening={editOpening} onSaved={() => loadOpenings().catch(() => {})} />
    </div>
  );
};
